<template>
  <div class="companies-no-login-report">
    <div class="report-note">
      <div class="report-note-title">À noter</div>
      <div class="report-note-text">
        La table des logs de connexion est récente (quelques mois). Lorsque la
        "dernière connexion" est vide, cela signifie qu'aucune connexion n'a été
        enregistrée dans ce système depuis sa mise en place. Un utilisateur a pu
        se connecter avant cette date sans que ce soit visible ici. Les
        connexions de support@ficos.com sont exclues (comme le rapport
        hebdomadaire par email).
      </div>
    </div>

    <div class="reports-content" :class="{detailsOpen: !!selectedRow}">
      <div class="reports-grid-wrapper">
        <ag-grid-vue
          class="ag-theme-alpine reports-grid"
          :column-defs="columnDefs"
          :default-col-def="defaultColDef"
          :row-model-type="'infinite'"
          :cache-block-size="pageSize"
          :max-blocks-in-cache="5"
          :infinite-initial-row-count="1"
          :row-height="40"
          :header-height="44"
          :animate-rows="false"
          :suppress-cell-focus="true"
          :suppress-field-dot-notation="true"
          :overlay-loading-template="loadingOverlay"
          :overlay-no-rows-template="noRowsOverlay"
          @grid-ready="onGridReady"
          @cell-clicked="onCellClicked"
          @filter-changed="onFilterChanged"
        />
      </div>

      <aside v-if="selectedRow" class="details-panel">
        <div class="details-header">
          <div class="details-title">
            {{ $t('hit-admin.reports.details') }}
          </div>
          <hit-button
            prefix-icon="clear"
            color="panel"
            :tooltip="$t('hit-admin.reports.close')"
            @click="selectedRow = null"
          />
        </div>

        <div class="details-body">
          <div class="focus-card">
            <div class="focus-card-header">
              <div class="focus-label">{{ selectedCellField || '-' }}</div>
              <hit-button
                v-if="selectedCellField"
                prefix-icon="copy"
                color="panel"
                :tooltip="'Copier la valeur'"
                :hide-border="true"
                :remove-outline="true"
                :padding-value="1"
                @click="copySelectedValue"
              />
            </div>
            <div class="focus-value">
              <pre
                v-if="
                  selectedCellValue && typeof selectedCellValue === 'object'
                "
                class="mono pre"
                >{{ formatCellValuePretty(selectedCellValue) }}</pre
              >
              <span v-else class="plain">{{
                formatCellValue(selectedCellValue)
              }}</span>
            </div>
          </div>
        </div>
      </aside>
    </div>

    <div v-if="toastMessage" class="hit-toast" role="status" aria-live="polite">
      <span class="hit-toast-dot" />
      <span class="hit-toast-text">{{ toastMessage }}</span>
    </div>
  </div>
</template>

<script>
import {defineComponent} from 'vue';
import {AgGridVue} from 'ag-grid-vue3';
import {HitButton} from '@hit/components';
import {adminApi} from '@admin/services/adminApi';

export default defineComponent({
  name: 'CompaniesNoLoginReport',
  components: {AgGridVue, HitButton},
  emits: ['filters-changed', 'total-changed'],
  props: {
    filterText: {
      type: String,
      default: '',
    },
    weeks: {
      type: Number,
      default: 3,
    },
    includeNever: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      gridApi: null,
      gridColumnApi: null,
      pageSize: 50,
      gridColumns: [],
      selectedRow: null,
      selectedCellField: null,
      selectedCellValue: null,
      activeFilterCount: 0,
      activeFilterColumns: [],
      lastTotalRows: null,
      lastEmittedTotalRows: null,
      toastMessage: '',
      toastTimer: null,
      loadingOverlay:
        '<div class="ag-overlay-loading-center">Chargement…</div>',
      noRowsOverlay:
        '<div class="ag-overlay-loading-center">Aucune donnée</div>',
    };
  },
  computed: {
    defaultColDef() {
      return {
        sortable: true,
        resizable: true,
        flex: 1,
        minWidth: 120,
        filter: true,
        floatingFilter: false,
        suppressHeaderMenuButton: false,
        filterParams: {
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          debounceMs: 250,
        },
      };
    },
    columnDefs() {
      if (!this.gridColumns || this.gridColumns.length === 0) return [];
      return this.gridColumns.map((field) => ({
        field,
        headerName: field,
        tooltipField: field,
        cellClass: 'hit-modern-cell',
        valueFormatter: (p) => {
          const v = p?.value;
          if (v === null || v === undefined) return '';
          if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
            return v.replace('T', ' ').replace(/(\.\d+)?Z?$/, '');
          }
          if (typeof v === 'object') {
            try {
              return JSON.stringify(v);
            } catch {
              return String(v);
            }
          }
          return String(v);
        },
        cellRenderer: (p) => {
          const formatted = p?.valueFormatted;
          if (formatted === null || formatted === undefined) {
            const v = p?.value;
            if (v === null || v === undefined) return '';
            if (typeof v === 'object') {
              try {
                return JSON.stringify(v);
              } catch {
                return String(v);
              }
            }
            return String(v);
          }
          return typeof formatted === 'string' ? formatted : String(formatted);
        },
      }));
    },
  },
  methods: {
    getExportState() {
      const filterText = this.filterText || '';
      let filterModel = null;
      let sortModel = null;

      try {
        filterModel =
          this.gridApi && typeof this.gridApi.getFilterModel === 'function'
            ? this.gridApi.getFilterModel()
            : null;
      } catch {
        filterModel = null;
      }

      try {
        sortModel =
          this.gridApi && typeof this.gridApi.getSortModel === 'function'
            ? this.gridApi.getSortModel()
            : null;
      } catch {
        sortModel = null;
      }

      const primarySort = Array.isArray(sortModel) ? sortModel[0] : null;
      const sortBy = primarySort?.colId || null;
      const sortDir = primarySort?.sort || null;

      const activeColumns = Array.isArray(this.activeFilterColumns)
        ? this.activeFilterColumns
        : [];
      const hasColumnFilters = activeColumns.length > 0;
      const hasGlobalSearch = !!filterText && filterText.trim().length > 0;

      return {
        filterText,
        filterModel,
        columnFiltersJson: filterModel ? JSON.stringify(filterModel) : null,
        sortModel,
        sortBy,
        sortDir,
        hasActiveFilters: hasColumnFilters || hasGlobalSearch,
        activeColumns,
        filteredTotal: this.lastTotalRows,
        weeks: this.weeks || 3,
        includeNever: this.includeNever !== false,
      };
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.setupHorizontalScrollbarWheel();
      this.reloadGrid();
    },
    refreshOnly() {
      this.reloadGrid();
    },
    resetAll() {
      this.selectedRow = null;
      this.selectedCellField = null;
      this.selectedCellValue = null;
      if (!this.gridApi) return;
      if (typeof this.gridApi.setFilterModel === 'function') {
        this.gridApi.setFilterModel(null);
      }
      if (typeof this.gridApi.setSortModel === 'function') {
        this.gridApi.setSortModel([{colId: 'last_login', sort: 'asc'}]);
      }
      this.reloadGrid();
    },
    reloadGrid() {
      if (!this.gridApi) return;
      const datasource = this.buildDatasource();
      if (typeof this.gridApi.setGridOption === 'function') {
        this.gridApi.setGridOption('datasource', datasource);
        if (typeof this.gridApi.refreshInfiniteCache === 'function') {
          this.gridApi.refreshInfiniteCache();
        }
      } else if (typeof this.gridApi.setDatasource === 'function') {
        this.gridApi.setDatasource(datasource);
      }
    },
    setGridColumns(columns) {
      this.gridColumns = Array.isArray(columns) ? columns : [];
      if (!this.gridApi) return;
      const defs = this.columnDefs;
      if (typeof this.gridApi.setGridOption === 'function') {
        this.gridApi.setGridOption('columnDefs', defs);
      } else if (typeof this.gridApi.setColumnDefs === 'function') {
        this.gridApi.setColumnDefs(defs);
      }
    },
    buildDatasource() {
      const self = this;
      return {
        async getRows(params) {
          try {
            const startRow = params.startRow ?? 0;
            const endRow = params.endRow ?? self.pageSize;
            const pageSize = endRow - startRow;
            const page = Math.floor(startRow / pageSize) + 1;

            const sortModel = params.sortModel || [];
            const primarySort = sortModel[0] || null;
            const sortBy = primarySort?.colId || null;
            const sortDir = primarySort?.sort || null;

            const columnFilters = params.filterModel
              ? JSON.stringify(params.filterModel)
              : null;

            const res = await adminApi.getCompaniesNoLogin({
              page,
              pageSize,
              sortBy,
              sortDir,
              filterText: self.filterText || null,
              columnFilters,
              weeks: self.weeks || 3,
              includeNever: self.includeNever !== false,
            });

            self.lastTotalRows = res?.total ?? null;
            if (
              typeof self.lastTotalRows === 'number' &&
              self.lastTotalRows !== self.lastEmittedTotalRows
            ) {
              self.lastEmittedTotalRows = self.lastTotalRows;
              self.$emit('total-changed', {total: self.lastTotalRows});
            }

            if (res?.columns?.length) {
              self.setGridColumns(res.columns);
            } else if (self.gridColumns.length === 0 && res?.rows?.length) {
              self.setGridColumns(Object.keys(res.rows[0]));
            }

            const rows = (res?.rows || []).map((r) => ({...r}));
            params.successCallback(rows, res?.total ?? rows.length);
          } catch {
            params.failCallback();
          }
        },
      };
    },
    onCellClicked(event) {
      if (event && event.data) {
        this.selectedRow = event.data;
        this.selectedCellField =
          event.colDef?.field || event.column?.getColId?.() || null;
        this.selectedCellValue =
          this.selectedCellField &&
          Object.prototype.hasOwnProperty.call(
            event.data,
            this.selectedCellField,
          )
            ? event.data[this.selectedCellField]
            : event.value;
      }
    },
    onFilterChanged() {
      try {
        const model =
          this.gridApi && typeof this.gridApi.getFilterModel === 'function'
            ? this.gridApi.getFilterModel()
            : null;
        const cols =
          model && typeof model === 'object'
            ? Object.keys(model).filter((k) => model[k])
            : [];
        this.activeFilterColumns = cols;
        this.activeFilterCount = cols.length;
      } catch {
        this.activeFilterCount = 0;
        this.activeFilterColumns = [];
      }

      this.$emit('filters-changed', {
        count: this.activeFilterCount,
        columns: this.activeFilterColumns,
      });
      this.reloadGrid();
    },
    formatCellValue(value) {
      if (value === null || value === undefined) return '';
      if (typeof value === 'string') return value;
      if (typeof value === 'number') return String(value);
      if (typeof value === 'object') {
        try {
          return JSON.stringify(value);
        } catch {
          return String(value);
        }
      }
      return String(value);
    },
    formatCellValuePretty(value) {
      if (value === null || value === undefined) return '';
      if (typeof value !== 'object') return this.formatCellValue(value);
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return this.formatCellValue(value);
      }
    },
    async copySelectedValue() {
      try {
        const text =
          this.selectedCellValue && typeof this.selectedCellValue === 'object'
            ? this.formatCellValuePretty(this.selectedCellValue)
            : this.formatCellValue(this.selectedCellValue);
        await navigator.clipboard.writeText(text || '');
        this.showToast('Copié avec succès');
      } catch {
        this.showToast('Copie impossible');
      }
    },
    showToast(message) {
      this.toastMessage = message || '';
      if (this.toastTimer) {
        clearTimeout(this.toastTimer);
        this.toastTimer = null;
      }
      this.toastTimer = setTimeout(() => {
        this.toastMessage = '';
        this.toastTimer = null;
      }, 1800);
    },
    setupHorizontalScrollbarWheel() {
      try {
        if (!this.gridApi || typeof this.gridApi.getGui !== 'function') return;
        const gui = this.gridApi.getGui();
        if (!gui) return;
        const hScroll = gui.querySelector?.('.ag-body-horizontal-scroll');
        const hViewport = gui.querySelector?.(
          '.ag-body-horizontal-scroll-viewport',
        );
        const target = hViewport || hScroll;
        if (!target) return;
        target.addEventListener(
          'wheel',
          (e) => {
            const dy = e?.deltaY ?? 0;
            if (!dy) return;
            const dx = e?.deltaX ?? 0;
            if (dx) return;
            e.preventDefault?.();
            const viewport =
              hViewport ||
              gui.querySelector?.('.ag-body-horizontal-scroll-viewport') ||
              target;
            if (viewport) viewport.scrollLeft += dy;
          },
          {passive: false},
        );
      } catch {
        // ignore
      }
    },
  },
});
</script>

<style lang="scss" scoped>
/* Reuse the same look as other report components */
.companies-no-login-report {
  height: 100%;
  min-height: 0;
  display: grid;
  grid-template-rows: max-content minmax(0, 1fr);
  gap: 0.6rem;
}

.report-note {
  border-radius: 0.85rem;
  border: 1px solid var(--ho-border);
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(6px);
  padding: 0.65rem 0.75rem;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.04);
  border-left: 4px solid
    color-mix(in srgb, var(--color-accent, #00997c) 55%, #ffffff);
}

.report-note-title {
  font-weight: 750;
  font-size: 0.85rem;
  color: rgba(15, 23, 42, 0.92);
}

.report-note-text {
  margin-top: 0.2rem;
  font-size: 0.85rem;
  line-height: 1.2rem;
  color: rgba(100, 116, 139, 1);
}
.reports-content {
  min-height: 0;
  height: 100%;
  display: grid;
  gap: 1rem;
}
.reports-content.detailsOpen {
  grid-template-columns: minmax(0, 1fr) 22rem;
}
.reports-grid-wrapper {
  min-height: 0;
  height: 100%;
  position: relative;
}
.reports-content.detailsOpen .reports-grid-wrapper::after {
  content: '';
  position: absolute;
  top: 0;
  right: -0.5rem;
  width: 1px;
  height: 100%;
  background: var(--ho-divider);
  pointer-events: none;
}
.details-panel {
  min-height: 0;
  height: 100%;
  border-radius: 0.85rem;
  border: 1px solid var(--ho-border);
  background: rgba(248, 250, 252, 1);
  box-shadow:
    0 1px 1px rgba(16, 24, 40, 0.04),
    0 12px 30px rgba(16, 24, 40, 0.1);
  overflow: hidden;
  display: grid;
  grid-template-rows: max-content minmax(0, 1fr);
}
.details-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.55rem 0.75rem;
  background: var(--color-panel, #f3f4f6);
  border-bottom: 1px solid var(--ho-border);
  border-left: 4px solid var(--color-accent, #00997c);
}
.details-title {
  font-weight: 650;
  color: rgba(15, 23, 42, 1);
}
.details-body {
  padding: 0.75rem;
  overflow: auto;
  display: grid;
  gap: 0.75rem;
  align-content: start;
}
.focus-card {
  border-radius: 0.85rem;
  border: 1px solid var(--ho-border);
  background: #ffffff;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.04);
  overflow: hidden;
}
.focus-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  background: linear-gradient(
    180deg,
    rgba(2, 6, 23, 0.02) 0%,
    rgba(255, 255, 255, 1) 100%
  );
  border-bottom: 1px solid var(--ho-border);
}
.focus-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(100, 116, 139, 1);
}
.focus-value {
  padding: 0.75rem;
  font-size: 0.9rem;
  color: rgba(15, 23, 42, 1);
  word-break: break-word;
}
.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    'Liberation Mono', 'Courier New', monospace;
}
.pre {
  margin: 0.35rem 0 0;
  font-size: 0.75rem;
  line-height: 1.2rem;
  background: rgba(248, 250, 252, 1);
  border: 1px solid var(--ho-border);
  border-radius: 0.65rem;
  padding: 0.5rem 0.6rem;
  max-height: 18rem;
  overflow: auto;
  white-space: pre-wrap;
}
.reports-grid {
  width: 100%;
  height: 100%;
  border-radius: 0.5rem;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid var(--ho-border);
  box-shadow:
    0 1px 1px rgba(16, 24, 40, 0.04),
    0 8px 24px rgba(16, 24, 40, 0.06);
}
.reports-grid.ag-theme-alpine {
  --ag-foreground-color: rgba(15, 23, 42, 0.82);
  --ag-background-color: #ffffff;
  --ag-header-foreground-color: rgba(255, 255, 255, 0.96);
  --ag-header-background-color: color-mix(
    in srgb,
    var(--color-accent, #00997c) 96%,
    #0b3a33
  );
  --ag-odd-row-background-color: rgba(2, 6, 23, 0.02);
  --ag-row-hover-color: color-mix(
    in srgb,
    var(--color-accent, #00997c) 8%,
    #ffffff
  );
  --ag-selected-row-background-color: color-mix(
    in srgb,
    var(--color-accent, #00997c) 14%,
    #ffffff
  );
  --ag-border-color: var(--ho-border);
  --ag-input-border-color: var(--ho-border-strong);
  --ag-accent-color: var(--color-accent, #00997c);
  --ag-alpine-active-color: var(--color-accent, #00997c);
  --ag-font-size: 13px;
  --ag-font-family: var(--font-family, ui-sans-serif, system-ui, -apple-system);
  --ag-row-border-color: rgba(2, 6, 23, 0.05);
}
::deep(.reports-grid .ag-header) {
  border-bottom: 1px solid var(--ho-border);
  box-shadow: none !important;
}
::deep(.reports-grid .ag-header-row) {
  border-bottom: none !important;
}
::deep(.reports-grid .ag-header-cell) {
  background: var(--ag-header-background-color);
  padding-left: 12px;
  padding-right: 12px;
}
::deep(.reports-grid .ag-header-cell-text) {
  color: rgba(255, 255, 255, 0.96);
}
::deep(.reports-grid .ag-header-cell .ag-icon),
::deep(.reports-grid .ag-header-cell .ag-icon svg) {
  color: rgba(255, 255, 255, 0.96);
  fill: rgba(255, 255, 255, 0.96);
}
::deep(.reports-grid .ag-header-cell-filtered .ag-icon-filter),
::deep(.reports-grid .ag-header-cell-filtered .ag-icon-filter svg) {
  color: rgba(249, 115, 22, 0.95);
  fill: rgba(249, 115, 22, 0.95);
}
::deep(.reports-grid .ag-cell) {
  padding-left: 12px !important;
  padding-right: 12px !important;
  line-height: 1.35rem;
  display: flex;
  align-items: center;
  color: rgba(15, 23, 42, 0.82);
}
::deep(.reports-grid .ag-row-odd .ag-cell) {
  color: rgba(15, 23, 42, 0.72);
}
.hit-toast {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 50;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.92);
  color: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow:
    0 10px 30px rgba(0, 0, 0, 0.18),
    0 2px 6px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(8px);
  max-width: min(360px, calc(100vw - 32px));
}
.hit-toast-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--color-accent, #00997c) 85%, #ffffff);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.08);
  flex: 0 0 auto;
}
.hit-toast-text {
  font-size: 0.875rem;
  line-height: 1.1rem;
}
@media (max-width: 1200px) {
  .reports-content.detailsOpen {
    grid-template-columns: minmax(0, 1fr) 18rem;
  }
}
</style>
